<template>
  <div v-if="visible" class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-box">
      <div class="q-modal-header">
        <span class="q-modal-title">Devoluciones</span>
        <span v-if="dateFrom && dateTo" class="modal-rango">{{ dateFrom }} → {{ dateTo }}</span>
        <button class="modal-close" @click="$emit('close')">✕</button>
      </div>

      <div v-if="loading" class="modal-loading">Cargando...</div>
      <div v-else-if="!devoluciones.length" class="modal-empty">Sin devoluciones en el periodo seleccionado.</div>

      <div v-else class="modal-body">
        <div class="resumen-row">
          <span class="resumen-pill pill-dev">{{ devoluciones.length }} devolución{{ devoluciones.length !== 1 ? 'es' : '' }}</span>
          <span v-if="totalEur > 0" class="resumen-pill pill-eur">-{{ fmt(totalEur) }}</span>
          <span v-if="totalUsd > 0" class="resumen-pill pill-usd">-{{ fmtUsd(totalUsd) }}</span>
        </div>

        <div class="tabla-wrap">
          <table class="tabla-dev">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Producto</th>
                <th>Motivo</th>
                <th class="text-right">Importe</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in devoluciones" :key="d.devolucion_id">
                <td class="td-fecha">{{ fmtFecha(d.fecha) }}</td>
                <td class="td-cliente">
                  <div class="cliente-nombre">{{ d.usuario_nombre }}</div>
                  <div class="cliente-email">{{ d.usuario_email }}</div>
                </td>
                <td class="td-producto">{{ d.producto }}</td>
                <td><span :class="['motivo-badge', 'motivo-' + d.motivo]">{{ motivoLabel(d.motivo) }}</span></td>
                <td class="td-importe">
                  <span :class="d.moneda === 'USD' ? 'importe-usd' : 'importe-eur'">
                    -{{ d.moneda === 'USD' ? fmtUsd(d.importe) : fmt(d.importe) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import panelesService from "@/services/paneles.service";

export default {
  name: "ModalDetalleDevoluciones",
  props: {
    visible:   { type: Boolean, default: false },
    dateFrom:  { type: String,  default: null },
    dateTo:    { type: String,  default: null },
    tipsterId: { type: [Number, String], default: null },
    canAdmin:  { type: Boolean, default: false },
  },
  emits: ["close"],
  data() {
    return { loading: false, devoluciones: [] };
  },
  computed: {
    totalEur() { return this.devoluciones.filter(d => d.moneda !== 'USD').reduce((s, d) => s + parseFloat(d.importe || 0), 0); },
    totalUsd() { return this.devoluciones.filter(d => d.moneda === 'USD').reduce((s, d) => s + parseFloat(d.importe || 0), 0); },
  },
  watch: {
    visible(val) { if (val) this.cargar(); },
  },
  methods: {
    async cargar() {
      this.loading = true;
      this.devoluciones = [];
      try {
        const payload = {};
        if (this.dateFrom) payload.date_from = this.dateFrom;
        if (this.dateTo)   payload.date_to   = this.dateTo;
        let res;
        if (this.canAdmin && this.tipsterId) {
          res = await panelesService.devolucionesDetalleAdmin(this.tipsterId, payload);
        } else {
          res = await panelesService.devolucionesDetalle(payload);
        }
        this.devoluciones = res?.data ?? [];
      } catch {
        this.devoluciones = [];
      } finally {
        this.loading = false;
      }
    },
    fmt(n)    { return Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" }); },
    fmtUsd(n) { return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" }); },
    fmtFecha(f) {
      if (!f) return "-";
      const d = new Date(f);
      return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
    },
    motivoLabel(m) {
      return {
        duplicate:       "Duplicado",
        fraudulent:      "Fraude",
        requested_by_customer: "Solicitada",
        otro:            "Otro",
      }[m] ?? m;
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.55);
  z-index: 1050; display: flex; align-items: center;
  justify-content: center; padding: 16px;
}
.modal-box {
  background: var(--q-card); border: 1px solid var(--q-border);
  border-radius: 16px; width: 100%; max-width: 720px;
  max-height: 85vh; display: flex; flex-direction: column; overflow: hidden;
}
.q-modal-header {
  display: flex; align-items: center; gap: 10px;
  padding: 16px 20px; border-bottom: 1px solid var(--q-border); flex-shrink: 0;
}
.q-modal-title { font-size: 15px; font-weight: 700; flex: 1; color: #d4af37; }
.modal-rango   { font-size: 12px; opacity: .6; white-space: nowrap; }
.modal-close   { background: none; border: none; cursor: pointer; font-size: 16px; opacity: .5; padding: 4px 6px; color: var(--q-text); }
.modal-close:hover { opacity: 1; }
.modal-loading, .modal-empty { padding: 40px 20px; text-align: center; opacity: .6; font-size: 14px; }
.modal-body { overflow-y: auto; flex: 1; }

.resumen-row { display: flex; gap: 8px; padding: 12px 20px; border-bottom: 1px solid var(--q-border); flex-wrap: wrap; }
.resumen-pill { font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 20px; }
.pill-dev { background: rgba(239,83,80,.12); color: #ef5350; }
.pill-eur { background: rgba(239,83,80,.12); color: #ef5350; }
.pill-usd { background: rgba(239,83,80,.10); color: #e57373; }

.tabla-wrap { overflow-x: auto; }
.tabla-dev { width: 100%; border-collapse: collapse; font-size: 13px; }
.tabla-dev th {
  padding: 10px 16px; text-align: left; font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: .04em; opacity: .55;
  border-bottom: 1px solid var(--q-border); white-space: nowrap;
}
.tabla-dev td { padding: 10px 16px; border-bottom: 1px solid var(--q-border); vertical-align: middle; }
.tabla-dev tbody tr:last-child td { border-bottom: none; }
.tabla-dev tbody tr:hover { background: rgba(239,83,80,.04); }

.td-fecha   { white-space: nowrap; font-size: 12px; }
.cliente-nombre { font-weight: 600; font-size: 13px; }
.cliente-email  { font-size: 11px; opacity: .55; }
.td-producto { max-width: 160px; font-size: 12px; }
.td-importe  { text-align: right; white-space: nowrap; }
.text-right  { text-align: right; }

.importe-eur { color: #ef5350; font-weight: 700; font-variant-numeric: tabular-nums; }
.importe-usd { color: #e57373; font-weight: 700; font-variant-numeric: tabular-nums; }

.motivo-badge {
  display: inline-block; padding: 2px 9px; border-radius: 20px;
  font-size: 11px; font-weight: 600; white-space: nowrap;
}
.motivo-requested_by_customer { background: rgba(255,193,7,.15); color: #ffc107; }
.motivo-duplicate             { background: rgba(66,165,245,.15); color: #42a5f5; }
.motivo-fraudulent            { background: rgba(239,83,80,.15);  color: #ef5350; }
.motivo-otro                  { background: rgba(128,128,128,.12); color: var(--q-text); opacity: .7; }
</style>
